import { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import RequireSession from "@/components/auth/RequireSession";
import RequirePlatformSession from "@/components/auth/RequirePlatformSession";
import AppShell from "@/components/layout/AppShell";
import PlatformShell from "@/components/layout/PlatformShell";
import { SessionBootstrap } from "@/components/auth/SessionBootstrap";
import { AppReadySync } from "@/components/layout/AppReadySync";
import { RouteLoading } from "@/components/ui/RouteLoading";
import * as Pages from "@/routes/lazyPages";
function App() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <AppReadySync>
        <SessionBootstrap />
        <Routes>
          <Route path="/" element={<Pages.MarketingPage />} />
          <Route path="/features" element={<Pages.MarketingFeaturesPage />} />
          <Route path="/plans" element={<Pages.MarketingPlansPage />} />
          <Route path="/contact" element={<Pages.MarketingContactPage />} />
          <Route path="/about" element={<Pages.MarketingAboutPage />} />
          <Route path="/login" element={<Pages.LoginPage />} />
          <Route path="/platform/login" element={<Pages.PlatformLoginPage />} />
          <Route
            path="/platform"
            element={
              <RequirePlatformSession>
                <PlatformShell />
              </RequirePlatformSession>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Pages.PlatformDashboardPage />} />
            <Route path="customers" element={<Pages.PlatformCustomersPage />} />
            <Route
              path="customers/new"
              element={<Pages.PlatformCustomerCreatePage />}
            />
            <Route
              path="customers/:customerId"
              element={<Pages.PlatformCustomerDetailPage />}
            />
            <Route path="roles" element={<Pages.PlatformRolesPage />} />
            <Route path="operators" element={<Pages.PlatformOperatorsPage />} />
          </Route>
          <Route
            path="/app"
            element={
              <RequireSession>
                <AppShell />
              </RequireSession>
            }
          >
            <Route index element={<Navigate to="dashboard/group" replace />} />
            <Route
              path="dashboard/group"
              element={<Pages.GroupDashboardPage />}
            />
            <Route
              path="dashboard/company"
              element={<Pages.CompanyDashboardPage />}
            />
            <Route
              path="operations/tower"
              element={<Pages.ControlTowerPage />}
            />
            <Route
              path="operations/whatsapp"
              element={<Pages.WhatsAppMockPage />}
            />
            <Route
              path="operations/templates"
              element={<Pages.CommunicationTemplatesPage />}
            />
            <Route path="orders" element={<Pages.OrdersListPage />} />
            <Route path="orders/new" element={<Pages.OrderCreatePage />} />
            <Route path="orders/:orderId" element={<Pages.OrderDetailPage />} />
            <Route
              path="planning/vehicles"
              element={<Pages.VehicleBoardPage />}
            />
            <Route
              path="planning/containers"
              element={<Pages.ContainerPlannerPage />}
            />
            <Route
              path="planning/dispatch"
              element={<Pages.DispatchSheetPage />}
            />
            <Route
              path="planning/conflicts"
              element={<Pages.ConflictsPage />}
            />
            <Route path="trips" element={<Pages.TripsListPage />} />
            <Route path="trips/closure" element={<Pages.TripClosurePage />} />
            <Route path="trips/history" element={<Pages.TripHistoryPage />} />
            <Route
              path="trips/exceptions"
              element={<Pages.TripExceptionsPage />}
            />
            <Route path="trips/:tripId" element={<Pages.TripDetailPage />} />
            <Route path="pod" element={<Pages.PodInboxPage />} />
            <Route path="pod/:podId" element={<Pages.PodDetailPage />} />
            <Route path="invoices" element={<Pages.InvoiceQueuePage />} />
            <Route
              path="invoices/:invoiceId"
              element={<Pages.InvoiceDetailPage />}
            />
            <Route
              path="invoices/:invoiceId/preview"
              element={<Pages.InvoiceDetailPage />}
            />
            <Route
              path="masters/company"
              element={<Pages.CompanyMasterPage />}
            />
            <Route
              path="masters/customers"
              element={<Pages.GlobalCustomerMasterPage />}
            />
            <Route
              path="masters/reference"
              element={<Pages.ReferenceMastersPage />}
            />
            <Route
              path="finance/collections"
              element={<Pages.CollectionDashboardPage />}
            />
            <Route
              path="finance/collection-follow-ups"
              element={<Pages.CollectionFollowUpPage />}
            />
            <Route path="finance/ledger" element={<Pages.LedgerPage />} />
            <Route
              path="finance/receipts"
              element={<Pages.ReceiptEntryPage />}
            />
            <Route
              path="finance/billing-adjustments"
              element={<Pages.BillingAdjustmentsPage />}
            />
            <Route path="finance/ageing" element={<Pages.AgeingPage />} />
            <Route
              path="admin/company-letterhead"
              element={<Pages.CompanyLetterheadSettingsPage />}
            />
            <Route path="admin/access" element={<Pages.AccessAdminPage />} />
            <Route
              path="admin/customer-permissions"
              element={<Pages.CustomerPermissionsPage />}
            />
            <Route path="office/tasks" element={<Pages.OfficeTasksPage />} />
            <Route
              path="office/tasks/:taskId"
              element={<Pages.TaskDetailPage />}
            />
            <Route
              path="office/department-board"
              element={<Pages.DepartmentBoardPage />}
            />
            <Route
              path="office/approvals"
              element={<Pages.OfficeApprovalsPage />}
            />
            <Route
              path="office/approvals/:approvalId"
              element={<Pages.ApprovalDetailPage />}
            />
            <Route
              path="office/follow-ups"
              element={<Pages.OfficeFollowUpsPage />}
            />
            <Route
              path="office/follow-ups/:followUpId"
              element={<Pages.FollowUpDetailPage />}
            />
            <Route path="office/reminders" element={<Pages.RemindersPage />} />
            <Route
              path="office/escalations"
              element={<Pages.EscalationsPage />}
            />
            <Route
              path="office/escalation-rules"
              element={<Pages.EscalationRulesPage />}
            />
            <Route
              path="office/promise-to-pay"
              element={<Pages.PromiseToPayPage />}
            />
            <Route
              path="office/collector-performance"
              element={<Pages.CollectorPerformancePage />}
            />
            <Route
              path="office/documents"
              element={<Pages.DocumentRepositoryPage />}
            />
            <Route path="fleet/drivers" element={<Pages.FleetDriversPage />} />
            <Route
              path="fleet/drivers/:driverId"
              element={<Pages.DriverDetailPage />}
            />
            <Route
              path="fleet/utilization"
              element={<Pages.FleetUtilizationPage />}
            />
            <Route
              path="fleet/vehicles"
              element={<Pages.FleetVehiclesPage />}
            />
            <Route
              path="fleet/vehicles/:vehicleId"
              element={<Pages.VehicleDetailPage />}
            />
            <Route path="fleet/fuel" element={<Pages.FleetFuelPage />} />
            <Route
              path="fleet/routes"
              element={<Pages.FleetRoutePerformancePage />}
            />
            <Route
              path="workshop/maintenance"
              element={<Pages.WorkshopMaintenancePage />}
            />
            <Route
              path="workshop/maintenance/new"
              element={<Pages.WorkshopMaintenanceCreatePage />}
            />
            <Route
              path="workshop/maintenance/:requestId"
              element={<Pages.WorkshopMaintenanceDetailPage />}
            />
            <Route
              path="workshop/job-cards"
              element={<Pages.WorkshopJobCardsPage />}
            />
            <Route
              path="workshop/job-cards/:jobCardId"
              element={<Pages.WorkshopJobCardDetailPage />}
            />
            <Route
              path="workshop/service-schedule"
              element={<Pages.WorkshopServiceSchedulePage />}
            />
            <Route
              path="workshop/inventory"
              element={<Pages.WorkshopInventoryPage />}
            />
            <Route
              path="workshop/breakdowns"
              element={<Pages.WorkshopBreakdownsPage />}
            />
            <Route
              path="workshop/breakdowns/:breakdownId"
              element={<Pages.WorkshopBreakdownDetailPage />}
            />
            <Route
              path="workshop/vendor-bills"
              element={<Pages.WorkshopVendorBillsPage />}
            />
            <Route
              path="workshop/vendor-bills/:billId"
              element={<Pages.WorkshopVendorBillDetailPage />}
            />
            <Route
              path="workshop/downtime"
              element={<Pages.WorkshopDowntimePage />}
            />
            <Route
              path="workshop/estimates"
              element={<Pages.WorkshopEstimatesPage />}
            />
            <Route
              path="workshop/estimates/:estimateId"
              element={<Pages.WorkshopEstimateDetailPage />}
            />
            <Route
              path="workshop/escalations"
              element={<Pages.WorkshopEscalationsPage />}
            />
            <Route
              path="workshop/reports"
              element={<Pages.WorkshopReportsPage />}
            />
            <Route
              path="insights/feedback"
              element={<Pages.ClientFeedbackPage />}
            />
            <Route
              path="insights/reports"
              element={<Pages.ReportLibraryPage />}
            />
            <Route
              path="insights/scheduled-exports"
              element={<Pages.ScheduledExportsPage />}
            />
            <Route
              path="insights/workflow-notes"
              element={<Pages.WorkflowImprovementsPage />}
            />
            <Route
              path="insights/recommendations"
              element={<Pages.AiRecommendationsPage />}
            />
            <Route path="settings" element={<Pages.SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppReadySync>
    </Suspense>
  );
}
export { App as default };
